import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Activity, AlertCircle, Eye, EyeOff } from "lucide-react";

const BG =
  "https://images.unsplash.com/photo-1531972111231-7482a960e109?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmUlMjBnbGFzc3xlbnwwfHx8fDE3ODcxNjQyNTZ8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) nav("/");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left: brand / image */}
      <div className="relative hidden lg:block">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/80" />
        <div className="relative z-10 flex h-full flex-col justify-between p-12 text-primary-foreground">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded bg-primary-foreground text-primary">
              <Activity size={22} strokeWidth={2.5} />
            </div>
            <span className="font-heading text-2xl font-extrabold">PayDelta</span>
          </div>
          <div className="max-w-md">
            <h1 className="font-heading text-4xl font-black leading-tight">
              Every payroll, reconciled.
            </h1>
            <p className="mt-4 text-primary-foreground/80 leading-relaxed">
              Compare each pay register against prior runs, surface variances, and trace every
              finding to its root cause — with customer-scoped security.
            </p>
          </div>
          <div className="font-mono text-xs uppercase tracking-widest text-primary-foreground/60">
            Variance detection · Root-cause analysis · Audit trail
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="relative flex items-center justify-center p-8">
        <div className="absolute inset-x-0 top-0 h-1 bg-foreground" />
        <div className="absolute inset-0 grain-overlay opacity-[0.12] pointer-events-none" />
        <form onSubmit={submit} className="relative w-full max-w-sm" data-testid="login-form">
          <div className="mb-8 flex items-center gap-2.5">
            <div className="grid h-9 w-9 place-items-center rounded bg-primary text-primary-foreground">
              <Activity size={18} strokeWidth={2.5} />
            </div>
            <span className="font-heading text-xl font-extrabold">PayDelta</span>
          </div>

          <h2 className="font-heading text-3xl font-bold">Sign in</h2>
          <p className="mt-1 text-sm text-muted-foreground">Access your payroll variance portfolio.</p>

          {error && (
            <div
              data-testid="login-error"
              className="mt-5 flex items-start gap-2 rounded-md border border-neg/30 bg-neg/5 px-3 py-2.5 text-sm text-neg"
            >
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                autoComplete="email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  data-testid="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  className="pr-10"
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground transition-colors hover:text-foreground"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full rounded-md press"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
